import { adminRoute, buildLoginReturnQuery } from '../../config/paths';
import { adminOidcConfig, type AdminOidcConfig } from './oidcConfig';
import {
  absoluteAppUrl,
  accessTokenExpiry,
  authServerUrl,
  authorizeOptionsFor,
  buildAuthorizeUrl,
  buildCodeExchangeBody,
  buildEndSessionUrl,
  buildRefreshBody,
  codeChallengeS256,
  computeRefreshDelayMs,
  decideCallbackError,
  decodeJwtPayload,
  generateCodeVerifier,
  parseCallbackParams,
  parseOidcTransaction,
  parseTokenResponse,
  randomUrlSafeString,
  safeReturnTo,
  validateIdTokenClaims,
  type AuthorizeKind,
  type OidcTransaction,
  type TokenResponse,
} from './oidcCore';

/**
 * RBAC Phase 3 (item 5) — runtime OIDC session for the admin SPA (`VITE_ADMIN_AUTH=oidc`).
 *
 * - Access / refresh / id tokens live in this module's memory only (never storage).
 * - sessionStorage holds only the PKCE transaction for one authorize → callback round trip
 *   (removed as soon as the callback reads it) and two loop-guard timestamps.
 * - A reload has no tokens: the session gate runs a top-level `prompt=none` authorize; a
 *   `login_required` answer falls back to an interactive authorize (auth-server login_url).
 */

const TXN_KEY = 'c4y.admin.oidc.txn';
const REAUTHORIZE_AT_KEY = 'c4y.admin.oidc.reauthorizeAt';
const MFA_STEP_UP_AT_KEY = 'c4y.admin.oidc.mfaStepUpAt';
const PHR_STEP_UP_AT_KEY = 'c4y.admin.oidc.phrStepUpAt';

export const ADMIN_OIDC_TOKEN_EVENT = 'evolveos:admin-oidc-token';

type OidcTokens = {
  accessToken: string;
  refreshToken: string | null;
  idToken: string | null;
  expSec: number | null;
  issuedAtSec: number | null;
  claims: Record<string, unknown> | null;
};

let tokens: OidcTokens | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let issuerCache: string | null = null;

function session(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function emit(): void {
  try {
    window.dispatchEvent(new CustomEvent(ADMIN_OIDC_TOKEN_EVENT));
  } catch {
    /* ignore */
  }
}

function readTimestamp(key: string): number | null {
  const raw = session()?.getItem(key);
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : null;
}

function writeTimestamp(key: string, value: number): void {
  try {
    session()?.setItem(key, String(value));
  } catch {
    /* ignore */
  }
}

export function lastOidcReauthorizeAt(): number | null {
  return readTimestamp(REAUTHORIZE_AT_KEY);
}

export function lastOidcMfaStepUpAt(): number | null {
  return readTimestamp(MFA_STEP_UP_AT_KEY);
}

/** Last passkey (phr) step-up authorize — the loop guard for 403 PHR_REQUIRED (RBAC Phase 4). */
export function lastOidcPhrStepUpAt(): number | null {
  return readTimestamp(PHR_STEP_UP_AT_KEY);
}

// ── token store ──────────────────────────────────────────────────────────────────────────

export function getOidcAccessToken(): string | null {
  return tokens?.accessToken ?? null;
}

export function hasOidcRefreshToken(): boolean {
  return !!tokens?.refreshToken;
}

/** Decoded (unverified) access-token claims — for display only, never for authorization. */
export function getOidcAccessClaims(): Record<string, unknown> | null {
  return tokens?.claims ?? null;
}

export function clearOidcTokens(): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
  tokens = null;
  emit();
}

function storeTokens(res: TokenResponse, previous: OidcTokens | null): void {
  const nowSec = Math.floor(Date.now() / 1000);
  const claims = decodeJwtPayload(res.access_token);
  tokens = {
    accessToken: res.access_token,
    // Rotation: the new refresh token replaces the old one (keep the old only if none came back).
    refreshToken: res.refresh_token ?? previous?.refreshToken ?? null,
    idToken: res.id_token ?? previous?.idToken ?? null,
    expSec: accessTokenExpiry(res.access_token, res.expires_in, nowSec),
    issuedAtSec: claims && typeof claims.iat === 'number' ? claims.iat : nowSec,
    claims,
  };
  scheduleRefresh();
  emit();
}

function scheduleRefresh(): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
  if (!tokens?.refreshToken || tokens.expSec === null) return;
  const delay = computeRefreshDelayMs({ expSec: tokens.expSec, nowMs: Date.now(), issuedAtSec: tokens.issuedAtSec });
  refreshTimer = setTimeout(() => {
    void refreshOidcTokens();
  }, delay);
}

// ── endpoints ────────────────────────────────────────────────────────────────────────────

async function postToken(cfg: AdminOidcConfig, body: URLSearchParams): Promise<{ status: number; json: unknown }> {
  const res = await fetch(authServerUrl(cfg.authServerBase, '/oauth/token'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
    body: body.toString(),
    credentials: 'omit',
  });
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }
  return { status: res.status, json };
}

/** Exact `iss`: `VITE_AUTH_SERVER_ISSUER`, else auth-server discovery (cached). */
export async function resolveExpectedIssuer(cfg: AdminOidcConfig = adminOidcConfig()): Promise<string | null> {
  if (cfg.issuer) return cfg.issuer;
  if (issuerCache) return issuerCache;
  try {
    const res = await fetch(authServerUrl(cfg.authServerBase, '/.well-known/openid-configuration'), {
      headers: { Accept: 'application/json' },
      credentials: 'omit',
    });
    if (!res.ok) return null;
    const doc = (await res.json()) as { issuer?: unknown };
    issuerCache = typeof doc.issuer === 'string' && doc.issuer ? doc.issuer : null;
    return issuerCache;
  } catch {
    return null;
  }
}

/** One refresh shared by every caller; rotates the refresh token. `false` = no usable session. */
export function refreshOidcTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const current = tokens;
  if (!current?.refreshToken) return Promise.resolve(false);
  const cfg = adminOidcConfig();
  refreshInFlight = (async () => {
    try {
      const { status, json } = await postToken(cfg, buildRefreshBody({ refreshToken: current.refreshToken!, clientId: cfg.clientId }));
      const parsed = status === 200 ? parseTokenResponse(json) : null;
      if (!parsed) {
        // invalid_grant (revoked / reused / expired): the refresh token is dead.
        if (status >= 400 && status < 500 && tokens === current) clearOidcTokens();
        return false;
      }
      if (tokens !== current) return true; // a newer login replaced the session meanwhile
      storeTokens(parsed, current);
      return true;
    } catch {
      return false; // network: keep tokens; the next 401 decides
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

// ── authorize / callback ─────────────────────────────────────────────────────────────────

export function currentReturnTo(): string {
  if (typeof window === 'undefined') return '/';
  return safeReturnTo(buildLoginReturnQuery(window.location.pathname, window.location.search));
}

export function redirectUri(cfg: AdminOidcConfig = adminOidcConfig()): string {
  return absoluteAppUrl(window.location.origin, adminRoute(cfg.redirectPath));
}

/** The pending transaction (without consuming it) — the login page reuses its `returnTo`. */
export function peekOidcTransaction(): OidcTransaction | null {
  return parseOidcTransaction(session()?.getItem(TXN_KEY), Date.now());
}

/** Top-level redirect to auth-server `/oauth/authorize` (never resolves in practice). */
export async function startOidcAuthorize(options: {
  kind: AuthorizeKind;
  returnTo?: string;
  provider?: 'google' | 'bizauth';
}): Promise<void> {
  const cfg = adminOidcConfig();
  const txn: OidcTransaction = {
    state: randomUrlSafeString(),
    nonce: randomUrlSafeString(),
    codeVerifier: generateCodeVerifier(),
    returnTo: safeReturnTo(options.returnTo ?? currentReturnTo()),
    kind: options.kind,
    createdAt: Date.now(),
  };
  const url = buildAuthorizeUrl({
    authServerBase: cfg.authServerBase,
    clientId: cfg.clientId,
    redirectUri: redirectUri(cfg),
    state: txn.state,
    nonce: txn.nonce,
    codeChallenge: await codeChallengeS256(txn.codeVerifier),
    provider: options.provider,
    ...authorizeOptionsFor(options.kind),
  });
  session()?.setItem(TXN_KEY, JSON.stringify(txn));
  if (options.kind === 'mfa-step-up') writeTimestamp(MFA_STEP_UP_AT_KEY, Date.now());
  if (options.kind === 'phr-step-up') writeTimestamp(PHR_STEP_UP_AT_KEY, Date.now());
  window.location.assign(url);
}

/** Re-authorize after a 401 that a refresh could not fix (loop-guarded by the caller). */
export function reauthorizeAfterUnauthorized(): void {
  writeTimestamp(REAUTHORIZE_AT_KEY, Date.now());
  clearOidcTokens();
  void startOidcAuthorize({ kind: 'silent' });
}

/** Re-authorize with `max_age=0&prompt=login&acr_values=mfa`, returning to the current route. */
export function reauthorizeForMfa(): void {
  void startOidcAuthorize({ kind: 'mfa-step-up' });
}

/** 403 PHR_REQUIRED: a fresh passkey sign-in (acr_values=phr), back to this route. */
export function reauthorizeForPhr(): void {
  void startOidcAuthorize({ kind: 'phr-step-up' });
}

export type OidcCallbackResult =
  | { status: 'signed-in'; returnTo: string }
  | { status: 'redirecting' }
  | { status: 'error'; message: string };

const CALLBACK_ERROR_TEXT: Record<string, string> = {
  access_denied: 'Sign-in was refused. Your account may be disabled or not allowed to use the admin console.',
  invalid_target: 'The admin console requested a resource this client is not allowed to use. Contact an administrator.',
  login_required: 'Sign-in is required.',
  interaction_required: 'Additional verification is required.',
};

/** `/auth/callback`: validate state, exchange the code (PKCE), validate the id_token, keep tokens in memory. */
export async function completeOidcCallback(search: string): Promise<OidcCallbackResult> {
  const store = session();
  const txn = parseOidcTransaction(store?.getItem(TXN_KEY), Date.now());
  store?.removeItem(TXN_KEY); // single use, whatever happens next
  const params = parseCallbackParams(search);
  if (params.kind === 'invalid') return { status: 'error', message: 'The sign-in response was incomplete.' };
  if (!txn || params.state !== txn.state) {
    return { status: 'error', message: 'This sign-in response does not match a sign-in started in this tab. Start again.' };
  }

  if (params.kind === 'error') {
    const action = decideCallbackError(params.error, txn.kind);
    if (action === 'interactive') {
      await startOidcAuthorize({ kind: 'interactive', returnTo: txn.returnTo });
      return { status: 'redirecting' };
    }
    return {
      status: 'error',
      message: CALLBACK_ERROR_TEXT[params.error] ?? params.errorDescription ?? `Sign-in failed (${params.error}).`,
    };
  }

  const cfg = adminOidcConfig();
  let exchanged: { status: number; json: unknown };
  try {
    exchanged = await postToken(
      cfg,
      buildCodeExchangeBody({ code: params.code, redirectUri: redirectUri(cfg), clientId: cfg.clientId, codeVerifier: txn.codeVerifier }),
    );
  } catch {
    return { status: 'error', message: 'Could not reach the sign-in service. Check your connection and try again.' };
  }
  const parsed = exchanged.status === 200 ? parseTokenResponse(exchanged.json) : null;
  if (!parsed) return { status: 'error', message: 'The sign-in code could not be exchanged. Start again.' };
  if (!parsed.id_token) return { status: 'error', message: 'The sign-in response had no ID token.' };

  const issuer = await resolveExpectedIssuer(cfg);
  if (!issuer) return { status: 'error', message: 'Could not determine the sign-in issuer (discovery unavailable).' };
  const check = validateIdTokenClaims(parsed.id_token, {
    issuer,
    clientId: cfg.clientId,
    nonce: txn.nonce,
    nowSec: Math.floor(Date.now() / 1000),
  });
  if (!check.ok) return { status: 'error', message: `The sign-in response failed validation (${check.reason}).` };

  storeTokens(parsed, null);
  return { status: 'signed-in', returnTo: txn.returnTo };
}

// ── logout ───────────────────────────────────────────────────────────────────────────────

/** RP-initiated logout: drop memory tokens, then auth-server `/oauth/end_session` (revokes + ends the session). */
export function oidcLogout(): void {
  const cfg = adminOidcConfig();
  const idTokenHint = tokens?.idToken ?? null;
  clearOidcTokens();
  try {
    const s = session();
    s?.removeItem(TXN_KEY);
    s?.removeItem(REAUTHORIZE_AT_KEY);
    s?.removeItem(MFA_STEP_UP_AT_KEY);
    s?.removeItem(PHR_STEP_UP_AT_KEY);
  } catch {
    /* ignore */
  }
  window.location.assign(
    buildEndSessionUrl({
      authServerBase: cfg.authServerBase,
      clientId: cfg.clientId,
      postLogoutRedirectUri: absoluteAppUrl(window.location.origin, adminRoute(cfg.postLogoutPath)),
      idTokenHint,
      state: 'signed_out',
    }),
  );
}
